import { isRealBomLine } from "./quoteQuantities";
import {
  getObsoleteAlternateCount,
  getUnmatchedSuggestionCount,
  isObsoleteLine,
  isUnmatchedLine,
} from "./digiKeyColumns";

export const ISSUE_REPORT_COLUMNS = [
  { key: "item_number", label: "Item #" },
  { key: "reference_designator", label: "Reference Designator" },
  { key: "manufacturer_part_number", label: "Part Number" },
  { key: "issue_label", label: "Issue Type" },
  { key: "description", label: "Description" },
  { key: "recommended_action", label: "Recommended Action" },
  { key: "severity", label: "Severity" },
];

export const EMPTY_ISSUE_REPORT = {
  version: 1,
  generated_at: null,
  sources: [],
  summary: { total: 0, error: 0, warning: 0, info: 0 },
  issues: [],
};

export const normalizeIssueReport = (report) => {
  if (!report || typeof report !== "object") {
    return { ...EMPTY_ISSUE_REPORT };
  }

  if (typeof report === "string") {
    try {
      return normalizeIssueReport(JSON.parse(report));
    } catch (e) {
      return { ...EMPTY_ISSUE_REPORT };
    }
  }

  return {
    ...EMPTY_ISSUE_REPORT,
    ...report,
    summary: {
      ...EMPTY_ISSUE_REPORT.summary,
      ...(report.summary || {}),
    },
    issues: Array.isArray(report.issues) ? report.issues : [],
  };
};

export const getIssueCountBadge = (report) => {
  const normalized = normalizeIssueReport(report);
  const errorCount = normalized.summary.error || 0;
  if (errorCount > 0) {
    return errorCount;
  }
  return normalized.summary.total || 0;
};

export const SUGGESTION_ISSUE_TYPES = [
  "invalid_mpn",
  "needs_alternate_selection",
  "obsolete",
];

export const findBomLineForIssue = (issue, bomlines) => {
  if (!issue || !Array.isArray(bomlines)) {
    return null;
  }
  const mpn = String(issue.manufacturer_part_number || "")
    .trim()
    .toUpperCase();
  if (!mpn) {
    return null;
  }
  return (
    bomlines.find(
      (line) =>
        isRealBomLine(line) &&
        String(line.manufacturer_part_number || "")
          .trim()
          .toUpperCase() === mpn,
    ) || null
  );
};

export const getIssueSuggestionCount = (issue, line) => {
  if (!line) {
    return issue?.issue_type === "invalid_mpn" ? 0 : null;
  }
  if (
    isUnmatchedLine(line) ||
    issue?.issue_type === "invalid_mpn" ||
    issue?.issue_type === "needs_alternate_selection"
  ) {
    return getUnmatchedSuggestionCount(line);
  }
  if (isObsoleteLine(line) || issue?.issue_type === "obsolete") {
    return getObsoleteAlternateCount(line);
  }
  return null;
};

export const isIssueRowActionable = (issue, line) => {
  if (!issue || !SUGGESTION_ISSUE_TYPES.includes(issue.issue_type)) {
    return false;
  }
  if (issue.issue_type === "needs_alternate_selection") {
    if (line?.substitutes_loaded === true) {
      return getUnmatchedSuggestionCount(line) > 0;
    }
    return true;
  }
  return Number(getIssueSuggestionCount(issue, line) || 0) > 0;
};

export const getSeverityClass = (severity) => {
  switch (severity) {
    case "error":
      return "text-danger";
    case "warning":
      return "text-warning";
    default:
      return "text-muted";
  }
};

/** Excel ARGB fill + font colors aligned with Q-BOM result sheet palette */
export const getSeverityExcelColors = (severity) => {
  switch (severity) {
    case "error":
      return { fill: "FDDEDE", text: "B42318" };
    case "warning":
      return { fill: "FFEDAD", text: "B54708" };
    case "info":
      return { fill: "E9E9FC", text: "4B499F" };
    default:
      return { fill: "FFFFFF", text: "333333" };
  }
};

/** RGB fill + text for jspdf-autotable */
export const getSeverityPdfColors = (severity) => {
  switch (severity) {
    case "error":
      return { fill: [253, 222, 222], text: [180, 35, 24] };
    case "warning":
      return { fill: [255, 237, 173], text: [181, 71, 8] };
    case "info":
      return { fill: [233, 233, 252], text: [75, 73, 159] };
    default:
      return { fill: [255, 255, 255], text: [51, 51, 51] };
  }
};

export const getSeverityRowClass = (severity) => {
  switch (severity) {
    case "error":
      return "issue-row-error";
    case "warning":
      return "issue-row-warning";
    case "info":
      return "issue-row-info";
    default:
      return "";
  }
};

export const filterIssues = (issues, severityFilter) => {
  if (!severityFilter || severityFilter === "all") {
    return issues;
  }
  return issues.filter((issue) => issue.severity === severityFilter);
};

export const buildIssueReportRows = (report) => {
  return normalizeIssueReport(report).issues.map((issue) => ({
    item_number: issue.item_number ?? "",
    reference_designator: issue.reference_designator || "",
    manufacturer_part_number: issue.manufacturer_part_number || "",
    issue_label: issue.issue_label || issue.issue_type || "",
    description: issue.description || "",
    recommended_action: issue.recommended_action || "",
    severity: issue.severity || "",
  }));
};
